import React from 'react';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import { UseMinutasHistoricasResult } from '../../hooks/useMinutasHistoricas';

interface Props {
  /**
   * Estado y acciones del hook `useMinutasHistoricas`. Se inyecta para
   * que el caller pueda leer `hook.resultados` y renderizarlos a su
   * gusto (lista clickeable compacta en Armar Pedido, tarjetas en
   * Minutas, etc.).
   */
  hook: UseMinutasHistoricasResult;
  /**
   * IDs a excluir del resultado (típicamente, las minutas vigentes
   * que ya están en la lista principal de la pantalla).
   */
  idsExcluir?: Set<number>;
  /**
   * Render prop para los resultados. Se invoca pasando el array de
   * minutas (vacío hasta que el usuario apriete "Buscar").
   */
  renderResultados?: (resultados: any[]) => React.ReactNode;
  /**
   * Texto bajo el título del acordeón para explicar qué filtra (cada
   * pantalla lo ajusta a su caso). Si se omite, no se muestra.
   */
  descripcion?: string;
  /**
   * Mensaje cuando la búsqueda devuelve 0 resultados.
   */
  textoSinResultados?: string;
}

/**
 * Acordeón "Buscar minutas anteriores" reusable por Armar Pedido y la
 * pantalla de Minutas. Maneja el toggle, inputs de fecha y el botón
 * Buscar. La pintura de los resultados es responsabilidad del caller
 * vía `renderResultados`.
 */
const BuscadorMinutasHistoricasAcordeon: React.FC<Props> = ({
  hook,
  idsExcluir,
  renderResultados,
  descripcion,
  textoSinResultados = 'No se encontraron minutas anteriores en ese rango.',
}) => {
  const {
    mostrar,
    setMostrar,
    fechaDesde,
    setFechaDesde,
    fechaHasta,
    setFechaHasta,
    cargando,
    resultados,
    buscado,
    buscar,
    errorMensaje,
  } = hook;

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
      <button
        type="button"
        onClick={() => setMostrar((v) => !v)}
        className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 font-medium"
      >
        {mostrar ? (
          <ChevronDown className="w-4 h-4" />
        ) : (
          <ChevronRight className="w-4 h-4" />
        )}
        Buscar minutas anteriores
      </button>

      {mostrar && (
        <div className="mt-3 pl-6">
          {descripcion && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              {descripcion}
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Desde
              </label>
              <input
                type="date"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Hasta
              </label>
              <input
                type="date"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <button
                type="button"
                onClick={() => buscar(idsExcluir)}
                disabled={cargando}
                className="w-full px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Search className="w-4 h-4" />
                {cargando ? 'Buscando…' : 'Buscar'}
              </button>
            </div>
          </div>

          {errorMensaje && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-2">
              {errorMensaje}
            </p>
          )}

          {buscado && (
            <div className="mt-3">
              {resultados.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                  {textoSinResultados}
                </p>
              ) : (
                <>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {resultados.length} resultado(s).
                  </p>
                  {renderResultados?.(resultados)}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BuscadorMinutasHistoricasAcordeon;
